import type {
  Anomaly,
  AnomalyCategory,
  AnomalyDirection,
  AnomalyMetricProjection,
  AnomalyPlatform,
  RawAnomalyDto,
} from '../models/anomaly.types'
import { PLATFORM_LABELS } from '../models/platform'

import { buildAnomalyNarrative } from './anomalyNarrative'
import { buildAnomalyTrendSpec } from './anomalyTrendSpec'
import { resolvePolarity, type LowerIsBetterLookup } from './resolvePolarity'

import { toSnakeCase } from '@/shared/lib/string'

const NEW_WINDOW_MS = 24 * 60 * 60 * 1000

export interface MetricIndexFallback {
  recordId: number
  requestedMetric: string
  fallbackMetric: string
}

export interface AdaptAnomalyContext {
  formatMetric: (value: number, metricField: string) => string
  metricLabel: (metricField: string) => string
  isLowerBetter?: LowerIsBetterLookup
  now?: number | Date
  onMetricIndexFallback?: (event: MetricIndexFallback) => void
}

function finiteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

function numberAt(values: readonly unknown[], index: number): number | null {
  return finiteNumber(values[index])
}

function rangeAt(values: readonly unknown[], index: number): [number, number] | null {
  const candidate = values[index]
  if (!Array.isArray(candidate) || candidate.length < 2) return null
  const minimum = finiteNumber(candidate[0])
  const maximum = finiteNumber(candidate[1])
  return minimum !== null && maximum !== null ? [minimum, maximum] : null
}

function categoryOf(value: string): AnomalyCategory {
  return value === 'needs_attention' ? 'needs_attention' : 'informational'
}

function prettify(value: string): string {
  const locale = /[çğıöşüİıÇĞÖŞÜ]/u.test(value) ? 'tr-TR' : 'en-US'
  return value
    .replaceAll(/[_-]+/gu, ' ')
    .toLocaleLowerCase(locale)
    .replaceAll(/(^|\s)\p{L}/gu, (letter) => letter.toLocaleUpperCase(locale))
}

function splitSegment(value: string): { segmentName: string; platformLabel: string } {
  const [segment = '', platform = ''] = value.split(/\s*–\s*/u, 2)
  return { segmentName: segment.trim(), platformLabel: platform.trim() }
}

function platformOf(platformLabel: string): AnomalyPlatform {
  const key = platformLabel ? toSnakeCase(platformLabel) : 'default_workspace'
  const definition = PLATFORM_LABELS[key] ?? PLATFORM_LABELS.default_workspace
  if (!definition) {
    return {
      labelKey: 'anomalies.platform.workspace',
      label: platformLabel,
      iconKey: 'default_workspace',
    }
  }
  return { ...definition, label: platformLabel }
}

function timestamp(value: string): number | null {
  const normalized = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/u.test(value)
    ? `${value.replace(' ', 'T')}Z`
    : value
  const parsed = Date.parse(normalized)
  return Number.isNaN(parsed) ? null : parsed
}

function isNew(createdAt: string, now: number): boolean {
  const created = timestamp(createdAt)
  if (created === null) return false
  const age = now - created
  return age >= 0 && age < NEW_WINDOW_MS
}

function metricIndexOf(
  raw: RawAnomalyDto,
  context: AdaptAnomalyContext,
  forceIndex?: number,
): number | null {
  const metrics = raw.details.metrics
  if (metrics.length === 0) return null
  if (forceIndex !== undefined) {
    return Number.isInteger(forceIndex) && forceIndex >= 0 && forceIndex < metrics.length
      ? forceIndex
      : null
  }
  const matching = metrics.indexOf(raw.metric)
  if (matching >= 0) return matching
  const fallbackMetric = metrics[0]
  if (!fallbackMetric) return null
  context.onMetricIndexFallback?.({
    recordId: raw.id,
    requestedMetric: raw.metric,
    fallbackMetric,
  })
  return 0
}

function projectionAt(
  raw: RawAnomalyDto,
  context: AdaptAnomalyContext,
  metricIndex: number,
): AnomalyMetricProjection | null {
  const metricField = raw.details.metrics[metricIndex]
  if (!metricField) return null

  const currentValue = numberAt(raw.details.metric_values, metricIndex)
  const expectedRange = rangeAt(raw.details.expected_ranges, metricIndex)
  if (currentValue === null || expectedRange === null) return null

  const history = raw.details.last_7_days?.[metricIndex] ?? []
  const validHistory = history.map(finiteNumber).filter((value) => value !== null)
  const prevValue = validHistory.at(-1) ?? 0
  const deviationPercentage = prevValue === 0 ? 0 : ((currentValue - prevValue) / prevValue) * 100
  const direction: AnomalyDirection = currentValue < expectedRange[0] ? 'DOWN' : 'UP'
  const polarity = resolvePolarity(metricField, context.isLowerBetter)
  const metricLabel = context.metricLabel(metricField) || prettify(metricField)
  const formattedTarget = context.formatMetric(
    direction === 'DOWN' ? expectedRange[0] : expectedRange[1],
    metricField,
  )
  const trend = buildAnomalyTrendSpec({
    details: raw.details,
    metricIndex,
    metricField,
    startDate: raw.start_date,
    currentValue,
    expectedRange,
  })
  if (!trend) return null

  return {
    metricIndex,
    metricField,
    currentValue,
    prevValue,
    expectedRange,
    deviationPercentage,
    direction,
    polarity,
    narrative: buildAnomalyNarrative({
      metricLabel,
      formattedValue: context.formatMetric(currentValue, metricField),
      formattedTarget,
      direction,
      polarity,
    }),
    trend,
  }
}

/** Project one wire record onto one metric without importing Vue, stores or transport. */
export function adaptAnomaly(
  raw: RawAnomalyDto,
  context: AdaptAnomalyContext,
  forceIndex?: number,
): Anomaly | null {
  const metricIndex = metricIndexOf(raw, context, forceIndex)
  if (metricIndex === null) return null
  const projection = projectionAt(raw, context, metricIndex)
  if (!projection) return null
  const relatedProjections = raw.details.metrics.flatMap((_metric, index) => {
    const related = projectionAt(raw, context, index)
    return related ? [related] : []
  })

  const { segmentName, platformLabel } = splitSegment(raw.segment_name)
  const now = context.now instanceof Date ? context.now.getTime() : (context.now ?? Date.now())

  return {
    id: raw.id,
    workspaceId: raw.workspace_id,
    category: categoryOf(raw.category),
    relatedMetricFields: [...raw.details.metrics],
    relatedProjections,
    segmentName,
    subSegmentName: prettify(raw.sub_segment_name),
    subSegmentRaw: raw.sub_segment_name,
    platform: platformOf(platformLabel),
    isNew: isNew(raw.created_at, now),
    startDate: raw.start_date,
    createdAt: raw.created_at,
    ...projection,
  }
}
